import type { Career, PRSkill } from '../types/salesforce'
import type { FixedSkillRating } from '../components/SkillsEditor/types'
import { FIXED_SKILLS, salesforceToRating, ratingToSalesforce } from '../types/skills'

// CareerオブジェクトからfixedSkillRatingsを抽出
export function extractFixedSkillRatings(career: Career): Record<string, FixedSkillRating> {
  const ratings: Record<string, FixedSkillRating> = {}

  FIXED_SKILLS.forEach((skill) => {
    const fieldValue = career[skill.careerField as keyof Career] as string | undefined
    ratings[skill.careerField] = {
      rating: salesforceToRating(fieldValue),
      description: '', // 初期値は空文字列
    }
  })

  return ratings
}

// fixedSkillRatingsをCareer更新用のデータに変換
export function convertRatingsToCareerFields(ratings: Record<string, FixedSkillRating>): Record<string, string | null> {
  const careerFields: Record<string, string | null> = {}

  Object.entries(ratings).forEach(([field, skillRating]) => {
    if (skillRating.rating !== null) {
      const rating = skillRating.rating
      // UIUXと映像スキルの場合は、説明文付きの値を生成
      if (field === 'design_skill__c') {
        const ratingStr = ratingToSalesforce(rating)
        const descriptions: Record<string, string> = {
          S: 'ユーザーの本質的な課題を理解し、戦略レイヤーから思考した上で、ユーザーが求めるデザインとして実装できる。',
          A: 'ユーザーの本質的な課題を理解し、課題を解決するために必要な機能を提案し、ユーザーが求めるデザインとして実装できる。',
          B: 'ユーザーの本質的な課題を理解し、エンジニアが開発した機能を使いやすいデザインとして実装できる（チームと協働できる）。',
          C: 'ユーザーの本質的な課題を理解し、ユーザーが求めるデザインを実装できる。',
          D: '自分のアイデア・考えた課題をもとに、デザインを実装できる。',
          E: '経験なし',
        }
        careerFields[field] = `${ratingStr}:${descriptions[ratingStr]}`
      } else if (field === 'videoproduction_skill__c') {
        const ratingStr = ratingToSalesforce(rating)
        const descriptions: Record<string, string> = {
          S: '映像制作の先端技術を使いこなし、制作における全工程をリードできる。技術的・芸術的背景を踏まえた総合ディレクションが可能。',
          A: 'チーム開発経験や映像全体のディレクション・企画力があり、より高度な合成や色補正、照明・カメラワークなど映像制作の本格知識を活用できる。',
          B: 'モーショングラフィックスやカラーコレクションなど一歩踏み込んだ編集もでき、そこそこの完成度の動画を制作可能。',
          C: 'Premiereなどの主要ソフトを使って短い映像作品を作れる。基本的な撮影・編集スキルを有している。',
          D: '授業レベルの簡単な撮影・編集を経験している。',
          E: '経験なし',
        }
        careerFields[field] = `${ratingStr}:${descriptions[ratingStr]}`
      } else {
        // その他のスキルは単純なS/A/B/C/D/E
        careerFields[field] = ratingToSalesforce(rating)
      }
    } else {
      careerFields[field] = null
    }
  })

  return careerFields
}

// PRSkillから言語スキルのみを抽出（固定スキル項目以外）
// 言語スキルは、SkillGroup__cがnullで、かつ固定スキルに該当しないもの
export function extractLanguageSkills(skills: PRSkill[]): PRSkill[] {
  // 固定スキル項目のラベルセット（比較用）
  const fixedSkillLabels = new Set(FIXED_SKILLS.map((s) => s.label))

  return skills.filter((skill) => {
    // グループ化されているスキルは除外（旧データ）
    if (skill.SkillGroup__c) return false

    // 固定スキル項目に該当しないもののみ
    return !fixedSkillLabels.has(skill.Name || '')
  })
}

// 固定スキルレーティングをPRSkillオブジェクトに変換
export function convertFixedSkillsToPRSkills(
  fixedSkillRatings: Record<string, FixedSkillRating>,
  prSheetId: string | undefined,
): PRSkill[] {
  const prSkills: PRSkill[] = []

  FIXED_SKILLS.forEach((skill) => {
    const skillRating = fixedSkillRatings[skill.careerField]

    // レーティングが設定されているスキルのみPRSkillとして保存
    if (skillRating && skillRating.rating !== null) {
      prSkills.push({
        Id: undefined, // 新規作成または既存IDは後で設定
        PRSheet__c: prSheetId,
        Name: skill.label,
        Rating__c: skillRating.rating, // Rating__cは数値（1-6）
        Description__c: skillRating.description || '',
        SkillGroup__c: undefined,
        SortOrder__c: undefined,
      })
    }
  })

  return prSkills
}

// PRSkillオブジェクトから固定スキルレーティングを抽出（名前でマッピング）
export function extractFixedSkillsFromPRSkills(skills: PRSkill[]): Record<string, FixedSkillRating> {
  const ratings: Record<string, FixedSkillRating> = {}

  // まず全てのスキルを初期化（rating: null, description: ''）
  FIXED_SKILLS.forEach((skill) => {
    ratings[skill.careerField] = {
      rating: null,
      description: '',
    }
  })

  // PRSkillから該当するスキルを見つけて設定
  skills.forEach((prSkill) => {
    const matchingSkill = FIXED_SKILLS.find((s) => s.label === prSkill.Name)
    if (matchingSkill) {
      ratings[matchingSkill.careerField] = {
        rating: (prSkill.Rating__c ?? null) as FixedSkillRating['rating'], // Rating__cは数値（1-6）
        description: prSkill.Description__c || '',
      }
    }
  })

  return ratings
}

// 固定スキル、言語スキル、旧スキルをマージ（保存用）
export function mergeAllSkills(
  fixedSkillRatings: Record<string, FixedSkillRating>,
  languageSkills: PRSkill[],
  existingSkills: PRSkill[],
  prSheetId: string | undefined,
): { mergedSkills: PRSkill[]; deletedSkillIds: string[] } {
  const fixedSkillLabels = new Set(FIXED_SKILLS.map((s) => s.label))
  const deletedSkillIds: string[] = []

  // 既存のスキルから固定スキルとそうでないものを分離
  const existingFixedSkills = existingSkills.filter((skill) => fixedSkillLabels.has(skill.Name || ''))
  const existingNonFixedSkills = existingSkills.filter((skill) => !fixedSkillLabels.has(skill.Name || ''))

  // 固定スキルをPRSkillオブジェクトに変換（既存のIDを保持）
  const fixedSkillsArray: PRSkill[] = []
  const existingFixedSkillMap = new Map(existingFixedSkills.map((s) => [s.Name, s]))

  FIXED_SKILLS.forEach((skill) => {
    const skillRating = fixedSkillRatings[skill.careerField]

    if (skillRating && skillRating.rating !== null) {
      // レーティングが設定されている場合は保存
      const existingSkill = existingFixedSkillMap.get(skill.label)
      fixedSkillsArray.push({
        Id: existingSkill?.Id, // 既存IDを保持
        PRSheet__c: prSheetId,
        Name: skill.label,
        Rating__c: skillRating.rating, // Rating__cは数値（1-6）
        Description__c: skillRating.description || '',
        SkillGroup__c: undefined,
        SortOrder__c: undefined,
      })
    } else {
      // レーティングがnullの場合、既存のスキルがあれば削除対象
      const existingSkill = existingFixedSkillMap.get(skill.label)
      if (existingSkill?.Id && !existingSkill.Id.startsWith('temp-')) {
        deletedSkillIds.push(existingSkill.Id)
      }
    }
  })

  // グループ化されたスキルは旧データとして残す（言語スキルと固定スキル以外）
  const oldGroupedSkills = existingNonFixedSkills.filter((skill) => skill.SkillGroup__c)

  // マージ: 固定スキル + 言語スキル + 旧グループスキル
  const mergedSkills = [...fixedSkillsArray, ...languageSkills, ...oldGroupedSkills]

  return { mergedSkills, deletedSkillIds }
}
